import { ICallHistory } from 'src/components/CallHistory/interfaces';
import { EMIBreakUp, WaiveFeeType } from './EmiSchedule.type';
import { FeedbackHistory, Repayment } from './Overview.type';
import { IWAInteraction } from './Interactions.type';
import {
  ICallInteraction,
  IFieldCallInteraction,
  ISystemInteraction
} from './CommunicationHistory.type';
import { CASE_DETAILS_TABS_FEATURE_FLAG, CONTACT_BUCKET } from '../constants';
import { CustomerDetailWithBankAccount } from './CustomerDetailAndbankAccount.type';
import { RepaymentHistoryAPIResponse } from './RepaymentHistory.type';
import { ValueOf } from '@cp/src/types/CommonConstans';
import { IAddress, IAddressFeedback, IGeoLocation } from '../components/Addresses/interfaces';
import { GenericObject } from '@cp/src/types/CommonConstans';
import { EmiFeeActions } from '../components/EmiSchedule/interfaces';
import { RiskRating } from '@cp/pages/CaseDetails/components/CustomerSummary/interfaces';
import { IEscalation, IEscalationContent } from './Escalations.types';
import { TopAddressesData } from '../components/TopAddresses/interfaces';
import { AlertType } from '../components/CustomerSummary/constant';

export interface Email {
  emailAddress: string;
  source: string;
  createdAt: string;
  isNew: boolean;
  sourceText: string;
}

export interface Emails {
  emailAddress: string;
  source: Array<string>;
  createdAt: string;
  isNew: boolean;
  sourceText: string;
}

interface OutstandingAmount {
  amount: number;
  currency: string;
}

interface LoanAmount {
  amount: number;
  currency: string;
}

export interface PendingEmiSchedule {
  referenceScheduleId: string;
  totalAmount: number;
  principal: number;
  interest: number;
  startDate: string;
  endDate: string;
  dueDate: string;
  installmentType: string;
  isDetailRow?: boolean;
}

export interface PartPrePayment {
  referenceId: string;
  paidAmount: number;
  principal: number;
  interest: number;
  endDate: string;
  dueDate: string;
  type: string;
  status: string;
}

export interface CustomerDocument {
  referenceId: string;
  uri: string;
  type: keyof DocumentType;
  docContentType?: string;
  language?: string;
  isLoading?: boolean;
  isDynamic?: boolean;
  generateDocument?: () => void;
}
export interface FeeComponentAdjustment {
  dueReferenceId: string;
  dueComponentReferenceId: string;
  dueDate: string;
  feeType: string;
  feeAmount: number;
  feeGst: number;
  waivedAmount: number;
  waivedGst: number;
  actionType: string;
  deferredAmount: number;
  deferredGst: number;
  actionDate: string;
  remark: string;
  deferredAmountPending: number;
}

interface IAdditionalWaiverInfo {
  isWaivable: boolean;
  maxWaivableAmount: number;
  heldTillInDateTimeFormat: string;
  maxAmountAlreadyWaived: boolean;
  refundedAmount: number;
  maxRefundableAmount: number;
  holdAllowedTill: string;
}
export interface Due {
  emiNumber: number;
  paidAmount: number;
  totalAmount: number;
  heldTill: string;
  holdAmount: number;
  waivedAmount: number;
  toggleState?: boolean;
  referenceId: string;
  dueDate: string;
  payByDate: string;
  generationDate: string;
  status: string;
  totalDueAmount: number;
  unpaidDueAmount: number;
  principal: number;
  unpaidPrincipal: number;
  interest: number;
  unpaidInterest: number;
  processingFee: number;
  totalPenalty: number;
  unpaidPenalty: number;
  totalLateFeeAmount: number;
  totalLateFeeGst: number;
  totalUnpaidLateFeeAmount: number;
  totalUnpaidLateFeeGst: number;
  totalBounceFeeAmount: number;
  totalBounceFeeGst: number;
  totalUnpaidBounceFeeAmount: number;
  totalUnpaidBounceFeeGst: number;
  totalOverdueInterest: number;
  totalUnpaidOverdueInterest: number;
  totalPenalInterest: number;
  totalUnpaidPenalInterest: number;
  campaignReferenceId?: unknown;
  lastPaidDate?: string;
  unpaidTotalAmount?: number;
  initialFoir: number;
  currentFoir: number;
  paid: boolean;
  unPaid: boolean;
  penaltyApplied: boolean;
  isDetailRow?: boolean;
  totalPenaltyCharges: number;
  totalPenaltyChargesGst: number;
  totalUnpaidPenaltyCharges: number;
  totalUnpaidPenaltyChargesGst: number;
  penaltyPercent: number;
  totalWaiverWithGst?: number;
  totalHeldAmountWithGst?: number;
  additionalWaiverInfo: IAdditionalWaiverInfo;
  waiverOrHoldAmount: number;
  isMaxWaived: boolean;
  action: EmiFeeActions | null;
  showHoldTooltip: boolean;
  disableHoldBtn: boolean;
  isColendingFlow: boolean;
  holdAllowedTill: string;
  unpaidDays: number;
}

interface FlashAlerts {
  cibilAlerts: unknown[];
}

interface Branch {
  id: number;
  name: string;
  ifsc: string;
  bankCode: string;
  city: string;
  address: string;
  district: string;
  state: string;
  genericIfsc?: unknown;
  active: boolean;
}

export const ENACH_MANDATE_STATUSES = {
  SIGNED: 'SIGNED',
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
  DISABLED: 'DISABLED'
};

export interface CallBasedInteractions {
  accountNumber: string;
  recipientNumber: string;
  interactionTimestamp: string;
  tag: string;
}

export const PTP_STATUSES = {
  FULFILLED: 'FULFILLED',
  YET_TO_MATURE: 'YET_TO_MATURE',
  OVERRIDDEN: 'OVERRIDDEN',
  BROKEN_PROMISE: 'BROKEN_PROMISE',
  PARTIALLY_PAID: 'PARTIALLY_PAID'
};

export interface PtpStatus {
  promiseDate: string;
  promiseAmount: number;
  promiseStatus: string;
}

export interface EmploymentDetails {
  monthlyIncome: MonthlyIncome;
  employmentType: string;
  employerName: string;
}

export interface MonthlyIncome {
  amount: number;
  currency: string;
}

export interface IEscalationStatus {
  escalations: IEscalationContent[];
  status: string;
  totalEscalations: number;
}

export enum CaseStatus {
  ON_HOLD = 'ON_HOLD',
  ACTIVE = 'ACTIVE'
}

export interface CustomerDetailApiResponse {
  riskSegment: keyof typeof RiskRating;
  caseReferenceId: string;
  customerName: string;
  customerReferenceId: string;
  addresses: IAddress[];
  topAddresses: TopAddressesData;
  phoneNumber: string;
  phoneNumberCustomId?: string;
  dateOfBirth: string;
  loanAccountNumber: string;
  outstandingAmount: OutstandingAmount;
  currentDpd: number;
  unpaidDays: number;
  employmentDetails: EmploymentDetails;
  currentDpdBucket: string;
  loanAmount: LoanAmount;
  productType: string;
  loanType: string;
  productSubType?: unknown;
  loanTenureInMonths: number;
  totalRemainingInstallments: number;
  startDate: string;
  activationDate: string;
  closingDate?: unknown;
  status: CaseStatus;
  pendingEmiSchedules: PendingEmiSchedule[];
  partPrePayments: PartPrePayment[];
  repaymentHistory: Repayment[];
  callBasedInteractions: CallBasedInteractions[];
  promises: unknown[];
  dues: Due[];
  capturePtpDate?: unknown;
  writeOffType: string;
  ptpStatuses: unknown[];
  flashFeedback: FlashFeedback;
  flashAlerts: FlashAlerts;
  callHistory: ICallHistory[];
  latitude: string;
  longitude: string;
  mandateStatus: keyof typeof ENACH_MANDATE_STATUSES;
  retryEnachEnable: boolean;
  branch: Branch;
  outstandingPrincipal: number;
  outstandingInterest: number;
  unpaidDues: number;
  tags: string[];
  currentFoir: number;
  initialFoir: number;
  annualRateOfInterest: number;
  allDues: Due[] | PendingEmiSchedule[] | PartPrePayment[];
  ptpPerEmiCountMap: {
    [key: string]: number;
  };
  closedLoanAccountViews?: IPreviousLoanAccount[];
  loanCustomerType: string;
  assignedFieldAgent?: string;
  editAccessFlag?: boolean;
  escalationDetails?: IEscalationStatus;
  totalDuesCount: number;
}
export interface FlashFeedback {
  feedbackType: string;
  params: {
    callbackTime: string;
    callbackDate: string;
    promisedAmount: number;
    promiseStatus: string;
    promiseDate: string;
    promiseAmount: number;
  };
}

export interface IApiData {
  data: unknown;
  loading: boolean;
  error: boolean;
}

export interface ICaseDetails extends IApiData {
  data: CustomerDetailApiResponse;
}
export interface IRepaymentHistory extends IApiData {
  data: Repayment[];
}
export interface IFeedbackHistory extends IApiData {
  data: FeedbackHistory;
}

export interface IWAInteractions extends IApiData {
  data: IWAInteraction;
}

export interface IEscalations extends IApiData {
  data: IEscalation;
}

export interface ISystemInteractionsType extends IApiData {
  data: ISystemInteraction;
  loading: boolean;
}

export interface ITelephones extends IApiData {
  data: Array<ITelePhoneData>;
}

export interface IPendingFeeComponents extends IApiData {
  data: Array<PendingFeeComponentV1>;
}

export interface PendingFeeComponentV1 {
  dueReferenceId: string;
  dueDate: string;
  availableActions?: AvailableActionsEntity[] | null;
  feeDetails?: FeeDetailsEntity[] | null;
}

export interface FeeDetailsEntity {
  feeType: keyof typeof WaiveFeeType;
  totalOriginalFeeAmount: number;
  totalOriginalFeeGst: number;
  totalRevisedFeeAmount: number;
  totalRevisedFeeGst: number;
  totalAmountPending: number;
  totalGstPending: number;
  totalWaivedAmount: number;
  totalWaivedGst: number;
  totalDeferredAmount: number;
  totalDeferredGst: number;
  totalHeldAmount: number;
  totalHeldGst: number;
}

export interface AvailableActionsEntity {
  label: string;
  value: string;
}

export interface IFeeComponentAdjustments extends IApiData {
  data: Array<FeeComponentAdjustment>;
  loading: boolean;
}
export interface ICustomerDocuments extends IApiData {
  data: Array<CustomerDocument>;
  loading: boolean;
}

export interface ILegalDocument extends IApiData {
  data: CustomerDocument;
  loading: boolean;
}

export enum LIMIT_TYPE {
  DAILY = 'DAILY',
  HOURLY = 'HOURLY'
}

export interface IPhoneNumberLimit {
  type: LIMIT_TYPE;
  callsMade: number;
  callRemaining: number;
  maxCalls: number;
  nextCallingTime: string;
  remarks: string;
}

export type IPhoneNumberLimitRecord = Record<LIMIT_TYPE, IPhoneNumberLimit>;

export interface ITelePhoneData {
  type: string;
  source: string;
  sourceText: string[];
  number: string;
  numberCustomId: string;
  valid: boolean;
  telephoneScore: number;
  havingMaximumScore: boolean;
  new: boolean;
  callHistory: ICallHistory[];
  sources: string[];
  oldestSource: string;
  createdAt: string;
  isPoc: boolean;
  limit: IPhoneNumberLimitRecord;
  showCreatedBy: boolean;
  createdByName?: string;
  createdByEmail?: string;
  telephoneCustomerConfidence?: ICustomerConfidenceLevel;
  proofOfSource: {
    proofOfSourceUrl?: string;
    isProofOfSourceAvailable?: boolean;
  };
}

interface ITopAddresses extends IApiData {
  data: TopAddressesData;
}
interface IAddresses extends IApiData {
  data: IAddress[];
}
interface IEMIBreakUp {
  [id: string]: {
    data: Array<EMIBreakUp>;
    loading: boolean;
    error: boolean;
  };
}
interface IRepayment {
  [id: string]: {
    data: Array<Repayment>;
    loading: boolean;
    error: boolean;
  };
}

interface IMonthlyEnachApiResponse {
  LocalDateTime: string;
  amount: number;
  enachRequestStatus: string;
}

interface IMonthlyEnach extends IApiData {
  data: {
    enachHistory: IMonthlyEnachApiResponse[];
    month: string;
  };
}

export interface IEmiSummary {
  lastEmiDate?: string;
  emiAmount?: number;
}

export interface ICallInteractionType {
  data: ICallInteraction;
  loading: boolean;
}

export interface IFieldCallInteractionType {
  data: IFieldCallInteraction;
  loading: boolean;
}

export interface ISkipTracingData {
  phone: string;
  name: string;
  contactBucket: CONTACT_BUCKET;
  source: string;
  lastInteractionTag: string;
  referenceId: string;
}

export interface ISpadeData {
  number: string;
  source: string;
  referenceId: string;
}

export interface ISkipTracing extends IApiData {
  data: ISkipTracingData[];
}

export interface ISpade extends IApiData {
  data: ISpadeData[];
}

export interface IBankAccountsEntity {
  referenceId: string;
  beneficiaryName: string;
  accountNumber: string;
  ifscCode: string;
  bankName: string;
  type: string;
  status: string;
  disbursementType: boolean;
  verified: boolean;
  bankReferenceId: string;
}

export interface ICustomerInfo {
  referenceId: string;
  name: string;
  phoneNumber: string;
  panNumber: string;
  email: string;
  fatherName: string;
  dateOfBirth: string;
  age: number;
  employmentType: string;
  pinCode: string;
  createdAt: string;
  bankAccounts?: IBankAccountsEntity[];
  companyName?: string;
  monthlyIncome: {
    amount: number;
    currency: string;
  };
  // addresses:
}

export interface ICustomerInfoPayload {
  data: CustomerDetailWithBankAccount;
  loading: boolean;
  error: boolean;
}

export interface IRepaymentHistoryAPIResponse extends IApiData {
  data: RepaymentHistoryAPIResponse;
}

export interface IPreviousLoanAccount {
  caseReferenceId: string;
  startDate: string;
  accountNumber: string;
}

export interface CaseDetailsPageData {
  details?: ICaseDetails;
  telephones?: ITelephones;
  skipTracingData?: ISkipTracing;
  spadeData?: ISpade;
  repaymentHistory?: IRepaymentHistory;
  feedbackHistory?: IFeedbackHistory;
  pendingFeeComponents?: IPendingFeeComponents;
  feeComponentAdjustments?: IFeeComponentAdjustments;
  customerDocs?: ICustomerDocuments;
  legalDocs?: ILegalDocument;
  addresses?: IAddresses;
  topAddresses?: ITopAddresses;
  emiBreakup?: IEMIBreakUp;
  repaymentDue?: IRepayment;
  waInteractions?: IWAInteractions;
  escalations?: IEscalations;
  whatsapp?: ISystemInteractionsType;
  sms?: ISystemInteractionsType;
  ivr?: ISystemInteractionsType;
  email?: ISystemInteractionsType;
  pushNotification?: ISystemInteractionsType;
  emiSummary?: IEmiSummary;
  monthlyEnach?: IMonthlyEnach;
  callInteractions?: ICallInteractionType;
  fieldCallInteractions?: IFieldCallInteractionType;
  customerInfo?: ICustomerInfoPayload;
  repaymentsHistory?: IRepaymentHistoryAPIResponse;
  telephonesv2?: ITelephones;
  __internal__lastUpdatedAt: number;
  sherlock?: ISherlockData;
  emails: Email[];
  geolocationsAndAddresses: IAddressesGeolocations;
  similarGeolocations: ISimilarGeolocations;
  pause: IPause;
}
export interface PageData {
  [id: string]: CaseDetailsPageData | null;
}

type caseDetailsTabsValue = ValueOf<typeof CASE_DETAILS_TABS_FEATURE_FLAG>;

export interface ReduxCaseDetail {
  detailsApiLoading: boolean;
  pageData: PageData;
  caseDetailTabsFeatureFlag: Record<caseDetailsTabsValue, boolean>;
  secondarySources: ISecondarySources[];
  customerConfidence: ICustomerConfidence;
  callAttemptLimitAlert: ICallAttemptLimitAlert;
}

export interface ICallAttemptLimitAlert {
  alertType?: AlertType;
  alertMessage?: string | null;
  alertDetails?: ICallAttemptAlertDetail[] | null;
}

export interface ICallAttemptAlertDetail {
  phoneNumber: string;
  dailyRemainingCalls: number;
  dailyTotalCalls: number;
}

export interface ICustomerConfidenceLevel {
  label: string;
  level: string;
  name: string;
}

export interface ICustomerConfidence {
  telephoneCustomerConfidence: ICustomerConfidenceLevel[];
  longhornSecondarySources: ISecondarySources[];
}

export interface ISecondarySources {
  label: string;
  value: string;
}
export interface ISherlock {
  type: string;
  date: string;
  status: string;
  isNew: boolean;
  organisationName?: string;
  joiningDate?: string;
  exitDate?: string;
  businessName?: string;
  registeredNames?: Array<string>;
  email?: string;
  businessAddress?: string;
  businessTypes?: Array<string>;
  gstin?: string;
  infoText?: string;
  officeAddress?: string;
}
export interface ISherlockData {
  data: Array<ISherlock>;
}
export interface IAddressesGeolocations extends IApiData {
  data: GenericObject;
}
export interface IGeolocations extends IAddressesGeolocations {
  geoLocations: IGeoLocation[];
  addressFeedback: IAddressFeedbacks[];
}
export interface IAddressFeedbacks extends IApiData {
  addressFeedbacks: IAddressFeedback[];
}

export interface ISimilarGeolocations extends IApiData {
  clusterId: string;
  timestamps: number[];
}

export interface IPause extends IApiData {
  data: {
    isPaused: boolean;
    pauseTillDate: string;
  };
}
